/**
 * The open cases behind the deadline pill and the Cases badge (HANDOFF §0.2: the pill is the open case with the
 * nearest deadline).
 *
 * Source: `GET /cases/deadline-summary` (case.read, scope-filtered; API 0.16.0, REQUESTS #51a): the number of
 * open cases (all of them, no page) and the open stage due soonest.
 *
 * Live from the event stream (case and deadline events): an event refreshes the summary (and the dashboard and
 * case lists that show the same cases). While the stream is not open (reconnecting, offline) it polls every
 * minute; while it is, a slow safety refresh runs.
 */
import { useCallback } from "react";
import { useQueryClient } from "@tanstack/react-query";
import {
  getGetCaseDeadlineSummaryQueryKey,
  getGetDashboardQueryKey,
  getListCasesQueryKey,
  useGetCaseDeadlineSummary,
} from "@brevalt/api-client/customer";
import { useCan, useEvent, useStreamStatus } from "@brevalt/app-core";
import type { EventName } from "@brevalt/api-client/events";

export const POLL_MS = 60_000;
export const LIVE_SAFETY_MS = 5 * 60_000;

/** Polling while the stream is not open; a slow safety refresh while it is. */
export const casesRefetchInterval = (live: boolean): number => (live ? LIVE_SAFETY_MS : POLL_MS);

const CASE_EVENTS: ReadonlySet<EventName> = new Set<EventName>([
  "case.created",
  "case.updated",
  "case.stage.updated",
  "case.stage.submitted",
  "deadline.warning",
  "deadline.reached",
]);

export interface NextDeadline {
  caseId: string;
  humanId: string;
  stage: string;
  dueAt: string;
}

export interface OpenCases {
  allowed: boolean;
  count: number;
  next: NextDeadline | null;
}

export function useOpenCases(enabled = true): OpenCases {
  const can = useCan();
  const allowed = enabled && can("case.read");
  const live = useStreamStatus() === "open";
  const queryClient = useQueryClient();
  const refetchInterval = casesRefetchInterval(live);
  const summary = useGetCaseDeadlineSummary({ query: { enabled: allowed, refetchInterval } });
  const refresh = useCallback(
    (event: { name: EventName }) => {
      if (!CASE_EVENTS.has(event.name)) return;
      void queryClient.invalidateQueries({ queryKey: getGetCaseDeadlineSummaryQueryKey() });
      // The home page and the case list show the same cases.
      void queryClient.invalidateQueries({ queryKey: getGetDashboardQueryKey() });
      void queryClient.invalidateQueries({ queryKey: getListCasesQueryKey() });
    },
    [queryClient],
  );
  useEvent("*", refresh);
  if (!allowed) return { allowed, count: 0, next: null };
  const n = summary.data?.next;
  return {
    allowed,
    count: summary.data?.openCases ?? 0,
    next:
      n?.caseId && n.dueAt && n.stage
        ? { caseId: n.caseId, humanId: n.humanId ?? "", stage: n.stage, dueAt: n.dueAt }
        : null,
  };
}
